import { GarageAccount, GuestPassSaveRequest, ParkingPassPurchaseRequest, Reservation } from '@parkingbase/ecommerce-api-test';
import { GuestPassService } from '@/services/guest-pass.service';

import { create } from 'zustand';
import { startOfDay } from 'date-fns';
import { logger } from '@/lib/logger';

/** A guest pass is upcoming if it is active, not cancelled/void, and its end date has not passed. */
function isUpcomingGuestPass(gp: Reservation): boolean {
  if (!gp.active || gp.status === 'CANCELLED' || gp.status === 'VOID') return false;
  if (gp.checkoutBeforeTime) {
    return new Date(gp.checkoutBeforeTime) >= startOfDay(new Date());
  }
  return true;
}

const guestPassService = new GuestPassService();

interface GuestPassState {
  upcomingGuestPasses: Reservation[];
  pastGuestPasses: Reservation[];
  isLoading: boolean;
  hasFetchedGuestPasses: boolean;
  selectedAccount: GarageAccount | null;
  setSelectedAccount: (account: GarageAccount | null) => void;
  mobileNewPassRequested: boolean;
  requestMobileNewPass: () => void;
  clearMobileNewPassRequest: () => void;
  getGuestPasses: () => Promise<void>;
  cancelGuestPass: (company: string, guestPassId: string) => Promise<boolean>;
  createGuestPass: (company: string, payload: GuestPassSaveRequest) => Promise<Reservation | null>;
  purchaseParkingPass: (
    company: string,
    payload: ParkingPassPurchaseRequest,
    recaptchaToken?: string
  ) => Promise<boolean>;
  resendGuestPassCoupon: (company: string, couponId: string) => Promise<boolean>;
  reset: () => void;
}

export const useGuestPassStore = create<GuestPassState>((set, get) => ({
  upcomingGuestPasses: [],
  pastGuestPasses: [],
  isLoading: false,
  hasFetchedGuestPasses: false,
  selectedAccount: null,
  setSelectedAccount: (account) => set({ selectedAccount: account }),
  mobileNewPassRequested: false,
  requestMobileNewPass: () => set({ mobileNewPassRequested: true }),
  clearMobileNewPassRequest: () => set({ mobileNewPassRequested: false }),
  reset: () =>
    set({
      upcomingGuestPasses: [],
      pastGuestPasses: [],
      isLoading: false,
      hasFetchedGuestPasses: false,
      selectedAccount: null,
      mobileNewPassRequested: false,
    }),
  getGuestPasses: async () => {
    try {
      set({ isLoading: true });
      const guestPasses = await guestPassService.getGuestPasses();
      const upcoming: Reservation[] = [];
      const past: Reservation[] = [];

      for (const gp of guestPasses) {
        if (isUpcomingGuestPass(gp)) {
          upcoming.push(gp);
        } else {
          past.push(gp);
        }
      }

      set({ upcomingGuestPasses: upcoming, pastGuestPasses: past });
    } catch (error) {
      logger.error('Failed to fetch guest passes', error, {
        feature: 'guest-pass-store',
        route: 'getGuestPasses',
      });
    } finally {
      set({ isLoading: false, hasFetchedGuestPasses: true });
    }
  },
  cancelGuestPass: async (company: string, guestPassId: string) => {
    try {
      await guestPassService.cancelGuestPass(company, guestPassId);
      await get().getGuestPasses();
      return true;
    } catch (error) {
      logger.error('Failed to cancel guest pass', error, {
        feature: 'guest-pass-store',
        route: 'cancelGuestPass',
      });
      return false;
    }
  },
  createGuestPass: async (company: string, payload: GuestPassSaveRequest) => {
    try {
      const reservation = await guestPassService.createGuestPass(company, payload);
      await get().getGuestPasses();
      return reservation;
    } catch (error) {
      logger.error('Failed to create guest pass', error, {
        feature: 'guest-pass-store',
        route: 'createGuestPass',
      });
      return null;
    }
  },
  purchaseParkingPass: async (
    company: string,
    payload: ParkingPassPurchaseRequest,
    recaptchaToken?: string
  ) => {
    try {
      await guestPassService.purchaseParkingPass(company, payload, recaptchaToken);
      return true;
    } catch (error) {
      logger.error('Failed to purchase parking pass', error, {
        feature: 'guest-pass-store',
        route: 'purchaseParkingPass',
      });
      // Rethrow so the caller can surface the backend's message (e.g. "Card Connect
      // Merchant Account need setup") in the payment-failed modal, not a generic one (PB-1401).
      throw error;
    }
  },
  resendGuestPassCoupon: async (company: string, couponId: string) => {
    try {
      await guestPassService.resendGuestPassCoupon(company, couponId);
      return true;
    } catch (error) {
      logger.error('Failed to resend guest pass coupon', error, {
        feature: 'guest-pass-store',
        route: 'resendGuestPassCoupon',
      });
      return false;
    }
  },
}));
